<script setup lang="ts">
import { onErrorCaptured, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { ApmpApiError } from '~/shared/api/errors'
import { tracker } from '~/shared/observability/error-tracking'

/**
 * A per-route error boundary (frontend phase-13 §6).
 *
 * A render or lifecycle error in one page must not blank the application: the shell keeps
 * working, the page is replaced by a recoverable state with the correlation id (the only thing
 * support can search for), and "Try again" re-mounts the page. The error is reported once
 * through the tracker; it is not re-thrown, so nothing reaches the console twice.
 *
 * Navigating away clears the state -- a broken page must not haunt the next one.
 */
const { t } = useI18n()
const route = useRoute()

const failure = ref<unknown>(null)
const attempt = ref(0)

const correlationId = () =>
  failure.value instanceof ApmpApiError ? failure.value.correlationId : undefined

onErrorCaptured((error, _instance, info) => {
  failure.value = error
  tracker().capture(error, { info, route: route.fullPath })
  return false
})

watch(
  () => route.fullPath,
  () => {
    failure.value = null
  },
)

function retry() {
  failure.value = null
  attempt.value += 1
}
</script>

<template>
  <div v-if="failure" class="apmp-boundary" role="alert" data-testid="route-error-boundary">
    <ApmpEmptyState :title="t('errors.boundaryTitle')" :description="t('errors.boundaryBody')" icon="⚠️">
      <template #action>
        <div class="apmp-boundary__actions">
          <n-button type="primary" @click="retry">{{ t('common.retry') }}</n-button>
          <RouterLink to="/"><n-button>{{ t('errors.goHome') }}</n-button></RouterLink>
        </div>
        <p v-if="correlationId()" class="apmp-boundary__ref" data-ancillary>
          {{ t('errors.reference', { id: correlationId() }) }}
        </p>
      </template>
    </ApmpEmptyState>
  </div>
  <slot v-else :key="attempt" />
</template>

<style scoped>
.apmp-boundary { padding: 24px 0; }
.apmp-boundary__actions { display: flex; gap: 8px; flex-wrap: wrap; justify-content: center; }
.apmp-boundary__ref { margin: 12px 0 0; font-size: 14px; color: var(--apmp-text-muted, #667085); }
</style>
